/**
 * Global state management for message view UI states using signals.
 *
 * This store owns two things:
 * 1. Per-message UI state (debug panel, highlight) keyed by message id.
 * 2. The chat scroll surface.
 *
 * Scrolling is delegated to the `react-virtuoso` list rendered by `ChatMessagesView` (ELARA-854).
 * The view registers Virtuoso's imperative handle here via `setVirtuosoRef`, and Virtuoso's own
 * `followOutput` keeps the list pinned to the newest message while it streams. This store therefore
 * no longer owns a scroll container, scroll listeners, or a MutationObserver — it exposes
 * `scrollToBottom`, `scrollToMessage`, and the `isAtBottom` signal that drives the "scroll to latest"
 * button.
 */

import type { VirtuosoHandle } from 'react-virtuoso';

import type { AppFlowsContainer } from '@elara/client-core/flows';
import type { ChatMessage } from '@elara/client-core/models/ChatMessageModel';
import type { ChatsState } from '@elara/client-core/states/ChatsState';
import type { MessageUiState } from '@elara/client-core/states/MessageUiState';
import type { ToastState } from '@elara/client-core/states/ToastState';

import { createState } from '@elara/client-core/lib/signals-utils';
import { createMessageFeedbackState } from '@elara/client-core/states/MessageFeedbackState';
import { createMessageUiState } from '@elara/client-core/states/MessageUiState';

/** Virtuoso only accepts `'auto' | 'smooth'`; map our broader `ScrollBehavior` callers onto that. */
function toVirtuosoBehavior(behavior: ScrollBehavior): 'auto' | 'smooth' {
  return behavior === 'smooth' ? 'smooth' : 'auto';
}

export function createMessageViewState(chatsState: ChatsState, flows: AppFlowsContainer, toastState: ToastState) {
  // The bad-response dialog target + the SubmitMessageFeedback flow call are DOM-free and shared with
  // the React Native message list (ELARA-738), so they live in `MessageFeedbackState`; this store
  // delegates its feedback surface to that instance.
  const feedback = createMessageFeedbackState(flows, toastState);
  const state = createState({
    uiStates: {} as Record<string, MessageUiState>,
    messageUiState(msg: ChatMessage): MessageUiState {
      this.uiStates[msg.id] ??= createMessageUiState(msg, chatsState);
      return this.uiStates[msg.id]!;
    },
    /**
     * The `react-virtuoso` imperative handle for the active chat list, registered by
     * `ChatMessagesView`. `null` when the list is unmounted (no chat selected / loading / empty).
     */
    virtuoso: null as VirtuosoHandle | null,
    /**
     * Whether the list is scrolled to (or near) the newest message. Driven by Virtuoso's
     * `atBottomStateChange`; powers the "scroll to latest" button visibility.
     */
    isAtBottom: true,
    /**
     * Pixels of the feed's bottom edge covered by UI floating above the composer (the preference
     * prompt), including its gap. Written by that UI while it is shown, so the "scroll to latest"
     * button can sit above it instead of behind it. `0` when nothing floats there.
     */
    composerOverlayHeight: 0,
    /** ID of the message currently being highlighted (for the scroll-to-message effect). */
    highlightedMessageId: null as string | null,
    _highlightTimeout: undefined as ReturnType<typeof setTimeout> | undefined,

    /** Register (or clear, with `null`) the Virtuoso handle for the current chat list. */
    setVirtuosoRef(handle: VirtuosoHandle | null): void {
      this.virtuoso = handle;
    },

    /** Sink for Virtuoso's `atBottomStateChange`. */
    setIsAtBottom(atBottom: boolean): void {
      this.isAtBottom = atBottom;
    },

    reset(): void {
      this.uiStates = {};
      // A freshly-opened chat renders from the bottom (Virtuoso `initialTopMostItemIndex`), so treat it
      // as at-bottom until the list reports otherwise — avoids a spurious "scroll to latest" flash.
      this.isAtBottom = true;
      if (this._highlightTimeout) {
        clearTimeout(this._highlightTimeout);
        this._highlightTimeout = undefined;
      }
      this.highlightedMessageId = null;
    },

    /** Scrolls the list to the newest message. */
    scrollToBottom(behavior: ScrollBehavior = 'smooth'): void {
      this.virtuoso?.scrollToIndex({ index: 'LAST', align: 'end', behavior: toVirtuosoBehavior(behavior) });
    },

    /**
     * Scrolls a message into view by id and briefly highlights it. Works with the virtualized list:
     * the target row may not be mounted, so we resolve its index within `messagesList` and drive
     * Virtuoso's `scrollToIndex` rather than a DOM query. If the message hasn't loaded yet (e.g.
     * navigating in from the shortlist while the chat is still resolving), retry once shortly after.
     */
    scrollToMessage(
      messageId: string,
      behavior: ScrollBehavior = 'smooth',
      align: 'start' | 'center' = 'center',
    ): void {
      const tryScroll = (): boolean => {
        const index = chatsState.messagesList.findIndex((m) => m.id === messageId);
        if (index === -1 || !this.virtuoso) return false;
        this.virtuoso.scrollToIndex({ index, align, behavior: toVirtuosoBehavior(behavior) });
        // `viewId === msg.id` for persisted messages (the only ones targeted here), so highlight by id.
        this.highlightMessage(messageId);
        return true;
      };
      if (!tryScroll()) {
        setTimeout(tryScroll, 150);
      }
    },

    /** Highlights a message briefly by setting the `highlightedMessageId` signal. */
    highlightMessage(viewId: string): void {
      if (this._highlightTimeout) {
        clearTimeout(this._highlightTimeout);
      }
      this.highlightedMessageId = viewId;
      // Slightly longer than the pre-virtualization 500ms: with virtualization the row may mount only
      // after the scroll animation settles, so give the highlight time to be seen once it appears.
      this._highlightTimeout = setTimeout(() => {
        this.highlightedMessageId = null;
        this._highlightTimeout = undefined;
      }, 1500);
    },

    /**
     * Bad-response feedback dialog surface, delegated to the shared {@link createMessageFeedbackState}
     * so the web and React Native message lists submit feedback through one implementation. Exposed
     * with the original names/signatures so existing web callers (`MessageFeedbackBar`, `TripPage`)
     * are unchanged.
     */
    get feedbackDialogOpen(): boolean {
      return feedback.feedbackDialogOpen;
    },
    openFeedbackDialogForMessage(chatId: string, messageId: string) {
      feedback.openFeedbackDialogForMessage(chatId, messageId);
    },
    closeFeedbackDialog() {
      feedback.closeFeedbackDialog();
    },
    async submitFeedback(chatId: string, messageId: string, rating: 'good' | 'bad', comment?: string) {
      await feedback.submitFeedback(chatId, messageId, rating, comment);
    },
    async submitFeedbackForDialog(comment?: string) {
      await feedback.submitFeedbackForDialog(comment);
    },
  });
  return state;
}

/** The main reactive store for message view UI states. All properties are signals transparent to the user. */
export type MessageViewState = ReturnType<typeof createMessageViewState>;
